import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TaskHoldControl, HoldDetailsCard } from "../components/TaskHoldControl";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft, Timer, AlertTriangle, CheckCircle2, Receipt,
  FileText, ShieldCheck, IdCard, Image as ImageIcon, FileBox, Eye, Upload,
  Truck, Hash, ClipboardCheck, Save, X, Replace, ExternalLink, Phone,
  User, Car, FileCheck, ChevronRight, AlertOctagon,
} from "lucide-react";
import { Sales, Files, BackOffice, Tasks, Masters, apiErrorMessage } from "../lib/api";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";
import { formatAuditDate, formatAuditTime } from "../lib/auditFormat";
import { isImage, isPdf, DOC_TYPE_LABELS } from "../lib/deliveryFile";
import { getUploadPolicy, validateUpload, downloadUrlFor } from "../lib/uploadPolicy";

/**
 * Road Tax / RTO Workbench — Desktop-first cockpit.
 *
 * Layout:
 *   • White compact summary header (SR · Customer/Vehicle/Location · Status)
 *   • 4 horizontally aligned KPI cards (SLA Remaining, Delivery Date,
 *     Pending Docs, Uploaded Docs)
 *   • Left column (70%): RTO Processing → Registration Update → Required Documents
 *   • Right column (30%, sticky): Customer & Vehicle Quick Panel
 *   • Sticky bottom action bar:
 *     Mark Submitted to RTO · Open Delivery File · Mark Delay/Hold · Mark Registration Complete
 *
 * URL: /back-office/road-tax/:saleId
 */

const REQUIRED_DOCS = [
  { type: "tax_invoice",    label: "Tax Invoice",      icon: FileText,    critical: true },
  { type: "policy_doc",     label: "Insurance Policy", icon: ShieldCheck, critical: true },
  { type: "aadhar",         label: "Aadhaar Card",     icon: IdCard,      critical: true },
  { type: "pan",            label: "PAN Card",         icon: IdCard,      critical: false },
  { type: "address_proof",  label: "Address Proof",    icon: FileBox,     critical: false },
  { type: "passport_photo", label: "Customer Photo",   icon: ImageIcon,   critical: true },
  { type: "vin_photo",      label: "VIN Photo",        icon: ImageIcon,   critical: false },
  { type: "form_20",        label: "Form 20",          icon: FileCheck,   critical: false },
  { type: "form_22",        label: "Form 22",          icon: FileCheck,   critical: false },
  { type: "other",          label: "Other Document",   icon: FileBox,     critical: false },
];

function useTickerSec() {
  const [, set] = useState(0);
  useEffect(() => {
    const id = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
}

function fmtCountdown(min) {
  if (min == null || Number.isNaN(min)) return "—";
  const abs = Math.round(Math.abs(min));
  const d = Math.floor(abs / (60 * 9)); // assume 9-hour working day
  const remHr = Math.floor((abs - d * 60 * 9) / 60);
  const m = abs % 60;
  if (d > 0) return `${d}d ${remHr}h ${m}m`;
  if (remHr > 0) return `${remHr}h ${m}m`;
  return `${m}m`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return `${formatAuditDate(iso)} · ${formatAuditTime(iso)}`;
}

function fmtShortDate(iso) {
  if (!iso) return "—";
  return formatAuditDate(iso);
}

function maskAadhaar(v) {
  if (!v) return "—";
  const clean = String(v).replace(/\D/g, "");
  if (clean.length < 4) return "XXXX-XXXX-" + clean.padStart(4, "X");
  return `XXXX-XXXX-${clean.slice(-4)}`;
}

function maskPan(v) {
  if (!v) return "—";
  const s = String(v).toUpperCase();
  if (s.length <= 5) return s;
  // Show first letter + mask middle + last 4. Standard PAN style: XXXXX1234X
  return `XXXXX${s.slice(-5)}`;
}

export default function RoadTaxWorkbench() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations } = useApp();
  const [sale, setSale] = useState(null);
  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [regnTypes, setRegnTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [replaceForType, setReplaceForType] = useState(null);
  useTickerSec();   // keep SLA countdown live

  // ───── form state
  const [receipt, setReceipt] = useState("");
  const [regn, setRegn] = useState("");
  const [notes, setNotes] = useState("");

  const reload = useCallback(async () => {
    const [s, ts] = await Promise.all([
      Sales.get(saleId),
      Tasks.list({ sale_id: saleId, type: "ROAD_TAX", with_sla: "true" }),
    ]);
    setSale(s);
    setTask(ts[0] || null);
    const fs = await Files.list(saleId);
    setFiles(fs || []);
    setReceipt(s.road_tax?.receipt_no || "");
    setRegn(s.road_tax?.regn_no || "");
    setNotes(s.road_tax?.notes || "");
  }, [saleId]);

  useEffect(() => { reload(); }, [reload]);

  // Load Registration Type masters once — used by the sidebar Registration
  // Details panel to render "TR — Temporary Registration" style labels.
  useEffect(() => {
    Masters.list({ type: "regn_type" }).then(setRegnTypes).catch(() => setRegnTypes([]));
  }, []);

  const regnTypeLabel = useMemo(() => {
    const id = sale?.registration?.regn_type_id;
    if (!id) return null;
    const rt = regnTypes.find((m) => m.id === id);
    if (!rt) return null;
    return rt.code ? `${rt.code} — ${rt.name}` : rt.name;
  }, [regnTypes, sale?.registration?.regn_type_id]);

  if (!sale) {
    return <div className="py-12 text-center text-sm text-slate-500">Loading workbench…</div>;
  }
  if (!task) {
    return (
      <div className="py-12 text-center text-sm text-slate-500" data-testid="rtwb-no-task">
        Road Tax task isn’t available for this sale yet. Insurance must be completed first.
        <div className="mt-3">
          <Link to="/back-office" className="text-primary hover:underline">Back to Back Office</Link>
        </div>
      </div>
    );
  }

  const loc = locations.find((l) => l.id === sale.location_id);
  const rt = sale.road_tax || {};
  const submitted = !!rt.submitted_to_rto;
  const completed = task.status === "completed";
  const sla = task.sla || {};
  const fieldsLocked = !submitted || completed;
  // Sales Manager: read-only across every action + form input on this workbench.
  // File uploads are governed separately and remain enabled for them.
  const canAct = activeUser?.role === "BackOffice" || activeUser?.role === "Admin";
  const canComplete = submitted && !completed && receipt.trim() && regn.trim() && canAct;

  // KPI counts — based on REQUIRED_DOCS (the docs the user must furnish for RTO submission)
  const uploadedTypes = new Set(files.map((f) => f.type));
  const uploadedCount = REQUIRED_DOCS.filter((d) => uploadedTypes.has(d.type)).length;
  const pendingCount = REQUIRED_DOCS.length - uploadedCount;
  const missingCritical = REQUIRED_DOCS.filter(
    (d) => d.critical && !uploadedTypes.has(d.type),
  );

  // Status pill
  let statusLabel = "Pending With RTO";
  let statusTone = "amber";
  if (completed) {
    statusLabel = "Registration Completed";
    statusTone = "emerald";
  } else if (sla.status === "delayed") {
    statusLabel = "SLA Overdue";
    statusTone = "rose";
  } else if (submitted) {
    statusLabel = "RTO Processing";
    statusTone = "sky";
  } else {
    statusLabel = "Pending With RTO";
    statusTone = "amber";
  }

  // SLA pretty value + tone
  const slaActive = !!sla.budget_minutes && !completed;
  const slaValue = !sla.budget_minutes
    ? "—"
    : completed ? "Closed"
    : sla.status === "delayed" ? `+${fmtCountdown(Math.abs(sla.remaining_minutes))} over`
    : fmtCountdown(sla.remaining_minutes);
  const slaTone = !slaActive
    ? "slate"
    : sla.status === "delayed" ? "rose"
    : sla.status === "at_risk" ? "amber"
    : "blue";
  const slaSubtitle = sla.due_at
    ? `Due: ${fmtShortDate(sla.due_at)} · ${formatAuditTime(sla.due_at)}`
    : (!sla.budget_minutes ? "Not started" : "—");

  // Handlers
  const onToggleRto = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      await BackOffice.roadTaxSubmitToRto(sale.id, { submitted: next, user_id: activeUser?.id });
      toast.success(next ? "Submitted to RTO · SLA timer started" : "Submission reverted");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doComplete = async () => {
    setBusy(true);
    try {
      await BackOffice.roadTaxComplete(sale.id, {
        receipt_no: receipt, regn_no: regn,
        notes: notes || null, user_id: activeUser?.id,
      });
      toast.success("Registration completed · HSRP unlocked");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const onClickComplete = () => {
    if (!canComplete) return;
    if (missingCritical.length > 0) { setMissingOpen(true); return; }
    doComplete();
  };

  return (
    <div className="space-y-4 pb-28" data-testid="rtwb-page">
      <button
        onClick={() => navigate("/back-office")}
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
        data-testid="rtwb-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Back Office
      </button>

      {/* ───── Compact white summary header + KPI row ───── */}
      <Card className="border-slate-200 p-5" data-testid="rtwb-summary-header">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          {/* Identity block */}
          <div className="flex items-start gap-4 min-w-0">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Receipt className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              {/* Line 1: SR Number */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono-data text-lg font-bold tracking-tight text-slate-900"
                      data-testid="rtwb-sr-number">
                  {sale.sr_number || "—"}
                </span>
              </div>
              {/* Line 2: Customer Name · Vehicle Model · Location */}
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{sale.customer?.name || "—"}</span>
                <span className="text-slate-300">•</span>
                <span>{sale.vehicle?.model || "—"}</span>
                <span className="text-slate-300">•</span>
                <span className="inline-flex items-center gap-1 text-slate-600">
                  {loc?.name || "—"}{loc?.code ? ` (${loc.code})` : ""}
                </span>
              </div>
              {/* Line 3: Status badge */}
              <div className="mt-2">
                <StatusPill label={statusLabel} tone={statusTone} testid="rtwb-status-pill" />
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 lg:max-w-[820px]">
            <KpiCard
              icon={Timer}
              label="SLA Remaining"
              value={slaValue}
              sub={slaSubtitle}
              tone={slaTone}
              testid="rtwb-kpi-sla"
            />
            <KpiCard
              icon={Truck}
              label="Delivery Date"
              value={fmtShortDate(sale.delivery?.promised_date)}
              sub="Promised Delivery"
              tone="blue"
              testid="rtwb-kpi-delivery"
            />
            <KpiCard
              icon={FileText}
              label="Pending Documents"
              value={String(pendingCount)}
              sub={pendingCount === 0 ? "All uploaded" : "Upload required"}
              tone={pendingCount === 0 ? "emerald" : "rose"}
              testid="rtwb-kpi-pending"
            />
            <KpiCard
              icon={FileCheck}
              label="Uploaded Documents"
              value={`${uploadedCount} / ${REQUIRED_DOCS.length}`}
              sub="Documents uploaded"
              tone="emerald"
              testid="rtwb-kpi-uploaded"
            />
          </div>
        </div>

        {sla.status === "delayed" && !completed && (
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200"
               data-testid="rtwb-delay-banner">
            <AlertOctagon className="h-3.5 w-3.5" /> Delayed by {fmtCountdown(Math.abs(sla.remaining_minutes))} —
            SLA breached. Please complete on priority.
          </div>
        )}
      </Card>

      {/* ───── 70 / 30 grid ───── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-10">
        {/* LEFT — main workflow */}
        <div className="space-y-4 lg:col-span-7">

          {/* Hold details */}
          <HoldDetailsCard task={task} />

          {/* Section 1 — RTO Processing */}
          <Card className="border-slate-200 p-5" data-testid="rtwb-rto-status">
            <CardTitle icon={Receipt} label="RTO Processing" sub="Track and update RTO submission status" />
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Task Created" value={fmtDateTime(task.created_at)} mono />
              <Field
                label="Submitted to RTO"
                value={rt.submitted_to_rto_at ? fmtDateTime(rt.submitted_to_rto_at) : "—"}
                mono
              />
              <Field label="SLA Due" value={sla.due_at ? fmtDateTime(sla.due_at) : "—"} mono />
            </div>
            {!completed && canAct && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                <div className="text-sm font-semibold text-slate-800">
                  Submitted to RTO?
                </div>
                <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white">
                  <button
                    type="button"
                    onClick={() => onToggleRto(false)}
                    disabled={busy}
                    data-testid="rtwb-toggle-no"
                    className={cn(
                      "px-5 py-1.5 text-sm font-semibold transition-colors",
                      !submitted ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50",
                    )}
                  >No</button>
                  <button
                    type="button"
                    onClick={() => onToggleRto(true)}
                    disabled={busy}
                    data-testid="rtwb-toggle-yes"
                    className={cn(
                      "px-5 py-1.5 text-sm font-semibold transition-colors",
                      submitted ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50",
                    )}
                  >Yes</button>
                </div>
              </div>
            )}
            {!submitted && !completed && (
              <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
                Registration fields will activate after submission to RTO.
              </div>
            )}
          </Card>

          {/* Section 2 — Registration Update */}
          <Card className="border-slate-200 p-5" data-testid="rtwb-registration">
            <CardTitle icon={Hash} label="Registration Update" sub="Update registration details once completed" />
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Tax Receipt Number *</Label>
                <Input value={receipt} onChange={(e) => setReceipt(e.target.value)}
                  disabled={fieldsLocked || !canAct} placeholder="e.g. RT-OD-26-001"
                  className={cn("font-mono-data", (fieldsLocked || !canAct) ? "opacity-60" : "")}
                  data-testid="rtwb-receipt" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Registration Number *</Label>
                <Input value={regn} onChange={(e) => setRegn(e.target.value.toUpperCase())}
                  disabled={fieldsLocked || !canAct} placeholder="e.g. OD05AB1234"
                  className={cn("font-mono-data", (fieldsLocked || !canAct) ? "opacity-60" : "")}
                  data-testid="rtwb-regn" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Notes</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                  disabled={fieldsLocked || !canAct} placeholder="Add any notes (optional)"
                  className={(fieldsLocked || !canAct) ? "opacity-60" : ""}
                  data-testid="rtwb-notes" />
              </div>
            </div>
          </Card>

          {/* Section 3 — Required Documents (compressed) */}
          <RequiredDocsCard
            files={files}
            saleId={sale.id}
            userId={activeUser?.id}
            readOnly={completed}
            onPreview={setPreviewFile}
            onReload={reload}
            replaceForType={replaceForType}
            setReplaceForType={setReplaceForType}
          />

          {/* RTO Documents (Tax Receipt + RC) — surfaces only after submission */}
          {submitted && (
            <RtoDocsCard
              files={files}
              saleId={sale.id}
              userId={activeUser?.id}
              readOnly={completed}
              onPreview={setPreviewFile}
              onReload={reload}
            />
          )}
        </div>

        {/* RIGHT — Customer & Vehicle Quick Panel (sticky) */}
        <div className="lg:col-span-3">
          <Card className="border-slate-200 p-5 lg:sticky lg:top-4" data-testid="rtwb-quick-panel">
            <div className="flex items-center justify-between gap-3">
              <CardTitle icon={User} label="Customer & Vehicle Details" />
              <Link
                to={`/sales/${sale.id}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="rtwb-view-full"
              >
                Full <ChevronRight className="h-3 w-3" />
              </Link>
            </div>

            {/* Customer block */}
            <div className="mt-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Customer Details
              </div>
              <dl className="mt-3 space-y-2.5">
                <PanelRow label="Customer Name" value={sale.customer?.name} bold />
                <PanelRow
                  label="Mobile"
                  value={sale.customer?.phone}
                  mono
                  action={sale.customer?.phone && (
                    <a
                      href={`tel:${sale.customer.phone}`}
                      className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                      data-testid="rtwb-call-customer"
                      title="Call customer"
                    >
                      <Phone className="h-3 w-3" />
                    </a>
                  )}
                />
                <PanelRow label="Alternate Mobile" value={sale.customer?.alt_phone} mono />
                <PanelRow label="Aadhaar No." value={maskAadhaar(sale.customer?.aadhaar)} mono />
                <PanelRow label="PAN No." value={maskPan(sale.customer?.pan)} mono />
                <PanelRow
                  label="Address"
                  value={[
                    sale.customer?.address,
                    sale.customer?.city,
                    sale.customer?.district,
                    sale.customer?.state,
                    sale.customer?.pin_code,
                  ].filter(Boolean).join(", ")}
                  multiline
                />
              </dl>
            </div>

            <div className="my-5 h-px bg-slate-100" />

            {/* Vehicle block */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1.5">
                <Car className="h-3.5 w-3.5" /> Vehicle Details
              </div>
              <dl className="mt-3 space-y-2.5">
                <PanelRow label="Model" value={sale.vehicle?.model} bold />
                <PanelRow label="VIN" value={sale.vehicle?.vin} mono />
                <PanelRow label="Engine No." value={sale.vehicle?.engine_no} mono />
                <PanelRow label="Fuel" value={sale.vehicle?.fuel_type || "Petrol"} />
                <PanelRow label="Delivery Date" value={fmtShortDate(sale.delivery?.promised_date)} mono />
                <PanelRow label="Location" value={loc ? `${loc.name} (${loc.code})` : "—"} />
              </dl>
            </div>

            {/* Registration Details — highlighted bordered card. Sits between
                Vehicle Details and the "View Full Sale Details" link so RTO
                operators can eyeball Choice No · Regn Type · RTO Office at
                a glance without opening the full sale page. */}
            <div
              className="mt-4 rounded-lg border border-primary/25 bg-primary/[0.04] p-3"
              data-testid="rtwb-registration-panel"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary flex items-center gap-1.5">
                <FileCheck className="h-3.5 w-3.5" /> Registration Details
              </div>
              <dl className="mt-2.5 space-y-2">
                <RegnKV
                  label="Choice Number"
                  value={sale.registration?.choice_no || "NA"}
                  mono
                  emphasis="normal"
                  testid="rtwb-regn-choice-no"
                />
                <RegnKV
                  label="Registration Type"
                  value={regnTypeLabel || "—"}
                  emphasis="strong"
                  testid="rtwb-regn-type"
                />
                <RegnKV
                  label="RTO Office"
                  value={sale.registration?.rto_office || "—"}
                  emphasis="strong"
                  testid="rtwb-regn-rto"
                />
              </dl>
            </div>

            <Link
              to={`/sales/${sale.id}`}
              className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10"
              data-testid="rtwb-view-full-bottom"
            >
              View Full Sale Details <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </Card>
        </div>
      </div>

      {/* ───── Sticky action bar ───── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur"
        data-testid="rtwb-action-bar"
      >
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="hidden sm:inline">
              Last updated
              {sale.updated_at && (
                <> <span className="font-mono-data text-slate-700">{fmtDateTime(sale.updated_at)}</span></>
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!completed && canAct && (
              <Button
                variant="outline"
                onClick={() => onToggleRto(!submitted)}
                disabled={busy || task?.sla_paused}
                data-testid="rtwb-bar-submit"
                className="gap-1.5"
              >
                <Save className="h-4 w-4" />
                {submitted ? "Revert Submission" : "Mark Submitted to RTO"}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/delivery-files/${sale.id}`)}
              data-testid="rtwb-bar-delivery"
              className="gap-1.5"
            >
              <ExternalLink className="h-4 w-4" /> Open Delivery File
            </Button>
            {canAct && <TaskHoldControl task={task} userId={activeUser?.id} onChanged={reload} />}
            {!completed && canAct && (
              <Button
                onClick={onClickComplete}
                disabled={!canComplete || busy || task?.sla_paused}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300"
                data-testid="rtwb-bar-complete"
              >
                <CheckCircle2 className="h-4 w-4" /> Mark Registration Complete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Missing-doc dialog */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Critical documents missing
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The following documents are usually required for registration handoff:
          </p>
          <ul className="list-disc pl-6 text-sm text-slate-700" data-testid="rtwb-missing-list">
            {missingCritical.map((d) => <li key={d.type}>{d.label}</li>)}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingOpen(false)}
              data-testid="rtwb-missing-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
              onClick={() => { setMissingOpen(false); doComplete(); }}
              data-testid="rtwb-missing-continue">Continue Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview drawer */}
      <Dialog open={!!previewFile} onOpenChange={(o) => !o && setPreviewFile(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span className="truncate">{previewFile?.original_filename || "Preview"}</span>
              <button onClick={() => setPreviewFile(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
          </DialogHeader>
          {previewFile && <FilePreview file={previewFile} />}
          {previewFile && (
            <DialogFooter>
              <a href={Files.url(previewFile.id)} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                 data-testid="rtwb-preview-open">
                <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
              </a>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── Sub-components ───────────

function CardTitle({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="font-display text-base font-semibold text-slate-900">{label}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={cn(
        "mt-0.5 text-sm text-slate-800",
        mono ? "font-mono-data" : "",
        !value && "italic text-slate-400",
      )}>
        {value || "—"}
      </div>
    </div>
  );
}

function PanelRow({ label, value, bold, mono, multiline, action }) {
  return (
    <div className={cn("flex gap-3", multiline ? "items-start" : "items-center")}>
      <dt className="w-[42%] flex-shrink-0 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className={cn(
        "flex-1 min-w-0 text-xs",
        bold ? "font-semibold text-slate-900" : "text-slate-800",
        mono ? "font-mono-data text-[13px]" : "text-[13px]",
        !value && "italic text-slate-400",
        multiline ? "whitespace-normal break-words" : "truncate",
      )}>
        <span className="inline-flex items-center">
          {value || "—"}
          {action}
        </span>
      </dd>
    </div>
  );
}

const STATUS_TONES = {
  amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  sky:     "bg-sky-50 text-sky-700 ring-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rose:    "bg-rose-50 text-rose-700 ring-rose-200",
  slate:   "bg-slate-100 text-slate-700 ring-slate-200",
};

// Registration Details row — stacked label/value pair for the highlighted
// panel below Vehicle Details. `emphasis="strong"` renders the value larger
// and bolder for Registration Type + RTO Office (per spec).
function RegnKV({ label, value, mono, emphasis = "normal", testid }) {
  return (
    <div data-testid={testid}>
      <dt className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </dt>
      <dd className={cn(
        "mt-0.5 break-words",
        emphasis === "strong"
          ? "text-[14px] font-bold text-slate-900"
          : "text-[13px] font-semibold text-slate-800",
        mono && "font-mono-data",
        (!value || value === "—") && "italic font-medium text-slate-400",
      )}>
        {value || "—"}
      </dd>
    </div>
  );
}

function StatusPill({ label, tone, testid }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1",
        STATUS_TONES[tone] || STATUS_TONES.slate,
      )}
      data-testid={testid}
    >
      <Timer className="h-3 w-3" /> {label}
    </span>
  );
}

const KPI_TONES = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-200",    icon: "bg-blue-100 text-blue-700" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", icon: "bg-emerald-100 text-emerald-700" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   icon: "bg-amber-100 text-amber-700" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    icon: "bg-rose-100 text-rose-700" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   ring: "ring-slate-200",   icon: "bg-slate-100 text-slate-700" },
};

function KpiCard({ icon: Icon, label, value, sub, tone, testid }) {
  const t = KPI_TONES[tone] || KPI_TONES.slate;
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-lg border bg-white p-3 ring-1",
        t.ring,
      )}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className={cn("mt-1 font-display text-xl font-semibold leading-tight", t.text)}>
          {value}
        </div>
        {sub && <div className="mt-0.5 truncate text-[10px] text-slate-500">{sub}</div>}
      </div>
      <span className={cn(
        "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md",
        t.icon,
      )}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
  );
}

function FilePreview({ file }) {
  const url = Files.url(file.id);
  if (isImage(file.content_type)) {
    return <img src={url} alt={file.original_filename} className="mx-auto max-h-[70vh] rounded" />;
  }
  if (isPdf(file.content_type)) {
    return <iframe src={url} title={file.original_filename} className="h-[70vh] w-full rounded border" />;
  }
  return (
    <div className="rounded-md bg-slate-50 p-6 text-center text-sm text-slate-600">
      Preview not available for this file type.
    </div>
  );
}

// ─────────── Required Documents (compressed table-style rows) ───────────

function RequiredDocsCard({ files, saleId, userId, readOnly, onPreview, onReload,
                            replaceForType, setReplaceForType }) {
  const byType = useMemo(() => {
    const m = {};
    for (const f of files) (m[f.type] ||= []).push(f);
    return m;
  }, [files]);

  return (
    <Card className="border-slate-200 p-5" data-testid="rtwb-docs">
      <CardTitle icon={ClipboardCheck} label="Required Documents"
        sub="Upload all required documents for RTO submission" />

      {/* Header row */}
      <div className="mt-4 hidden grid-cols-12 items-center gap-3 border-b border-slate-100 px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:grid">
        <div className="col-span-4">Document</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-3">Uploaded On</div>
        <div className="col-span-3 text-right">Actions</div>
      </div>

      <div className="mt-1 divide-y divide-slate-100">
        {REQUIRED_DOCS.map((d) => {
          const list = byType[d.type] || [];
          const last = list[0];
          const present = !!last;
          return (
            <div
              key={d.type}
              className={cn(
                "grid grid-cols-1 items-center gap-2 px-3 py-2 sm:grid-cols-12 sm:gap-3",
                !present && "bg-rose-50/40",
              )}
              data-testid={`rtwb-doc-${d.type}`}
            >
              {/* Document name */}
              <div className="flex items-center gap-2.5 sm:col-span-4">
                <span className={cn(
                  "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md",
                  present ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
                )}>
                  <d.icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-slate-900">{d.label}</span>
                    {d.critical && !present && (
                      <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700">
                        Critical
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="sm:col-span-2">
                <span className={cn(
                  "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  present ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
                )}>
                  {present ? "Uploaded" : "Missing"}
                </span>
              </div>

              {/* Uploaded On */}
              <div className="text-xs text-slate-500 sm:col-span-3">
                {present ? (
                  <span className="font-mono-data">
                    {formatAuditDate(last.uploaded_at)} · {formatAuditTime(last.uploaded_at)}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-start gap-1.5 sm:col-span-3 sm:justify-end">
                {present && (
                  <>
                    <button
                      type="button"
                      onClick={() => onPreview(last)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid={`rtwb-doc-preview-${d.type}`}
                    >
                      <Eye className="h-3 w-3" /> Preview
                    </button>
                    <a
                      href={downloadUrlFor(d.type, last, Files.url(last.id))} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid={`rtwb-doc-download-${d.type}`}
                    >
                      ↓ Download
                    </a>
                  </>
                )}
                {!readOnly && (
                  <UploadInput type={d.type} saleId={saleId} userId={userId}
                    label={present ? "Replace" : "Upload"}
                    icon={present ? Replace : Upload}
                    onDone={onReload}
                    busy={replaceForType === d.type}
                    setBusy={(v) => setReplaceForType(v ? d.type : null)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function UploadInput({ type, saleId, userId, label, icon: Icon, onDone, busy, setBusy }) {
  const inputId = `rtwb-up-${type}`;
  const policy = getUploadPolicy(type);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateUpload(type, file);
    if (err) { toast.error(err); e.target.value = ""; return; }
    setBusy(true);
    try {
      await Files.upload(saleId, type, file, userId);
      toast.success(`${DOC_TYPE_LABELS[type] || type} uploaded`);
      onDone();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <label
        htmlFor={inputId}
        className={cn(
          "inline-flex cursor-pointer items-center gap-1 rounded-md border border-primary/40 bg-white px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5",
          busy && "pointer-events-none opacity-60",
        )}
        data-testid={`rtwb-doc-upload-${type}`}
        title={policy.helper}
      >
        <Icon className="h-3 w-3" /> {busy ? "Uploading…" : label}
        {policy.pdfOnly && <span className="text-[9px] font-normal opacity-70">(.pdf)</span>}
      </label>
      <input id={inputId} type="file" className="hidden" onChange={onChange}
        accept={policy.acceptAttr} />
    </>
  );
}

// ─────────── RTO Documents Card (Tax Receipt + RC) ───────────

const RTO_DOCS = [
  { type: "tax_receipt", label: "Road Tax Receipt",         icon: Receipt,
    hint: "Receipt issued by the RTO acknowledging tax payment." },
  { type: "rc",          label: "Registration Certificate (RC)", icon: IdCard,
    hint: "Final RC document issued by RTO. Upload as soon as available." },
];

function RtoDocsCard({ files, saleId, userId, readOnly, onPreview, onReload }) {
  const [busyType, setBusyType] = useState(null);
  const byType = useMemo(() => {
    const m = {};
    for (const f of files) (m[f.type] ||= []).push(f);
    return m;
  }, [files]);

  return (
    <Card className="border-sky-200 bg-sky-50/30 p-5" data-testid="rtwb-rto-docs">
      <CardTitle icon={ClipboardCheck} label="RTO Documents"
        sub="Upload after submission · Road Tax Receipt + Registration Certificate" />
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {RTO_DOCS.map((d) => {
          const last = (byType[d.type] || [])[0];
          const present = !!last;
          return (
            <div key={d.type}
              className={cn(
                "rounded-lg border bg-white p-3",
                present ? "border-emerald-200" : "border-rose-200",
              )}
              data-testid={`rtwb-rto-doc-${d.type}`}
            >
              <div className="flex items-start gap-3">
                <span className={cn(
                  "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full",
                  present ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
                )}>
                  <d.icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">{d.label}</span>
                    <span className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                      present ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
                    )}>
                      {present ? "Uploaded" : "Missing"}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {present ? (
                      <>
                        Uploaded {formatAuditDate(last.uploaded_at)} · {formatAuditTime(last.uploaded_at)}
                        {last.uploaded_by_name && <> · by <span className="text-slate-700">{last.uploaded_by_name}</span></>}
                      </>
                    ) : d.hint}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {present && (
                  <>
                    <button
                      type="button"
                      onClick={() => onPreview(last)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid={`rtwb-rto-doc-preview-${d.type}`}
                    >
                      <Eye className="h-3 w-3" /> Preview
                    </button>
                    <a
                      href={downloadUrlFor(d.type, last, Files.url(last.id))} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid={`rtwb-rto-doc-download-${d.type}`}
                    >
                      ↓ Download
                    </a>
                  </>
                )}
                {!readOnly && (
                  <UploadInput type={d.type} saleId={saleId} userId={userId}
                    label={present ? "Replace" : "Upload"}
                    icon={present ? Replace : Upload}
                    onDone={onReload}
                    busy={busyType === d.type}
                    setBusy={(v) => setBusyType(v ? d.type : null)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
